import { useRef, useSyncExternalStore } from 'react';
import { StructureSetId } from '~/domain/changes/ids';
import { createRoiListItemView } from '~/domain/projections/factories';
import { RoiListItemView } from '~/domain/projections/views';
import { domainTrace } from '~/domain/diagnostics/domainTrace';
import { useDomainStore } from './useDomainStore';
import { usePatient } from './usePatient';

interface CacheEntry {
    version: number;
    ssId: StructureSetId;
    items: readonly RoiListItemView[];
}

export function useRoiListView(
    ssId: StructureSetId
): readonly RoiListItemView[] {
    const store = useDomainStore();
    const patient = usePatient();
    const cacheRef = useRef<CacheEntry | null>(null);

    return useSyncExternalStore(store.subscribe, () => {
        const version = store.getVersion();
        if (
            cacheRef.current &&
            cacheRef.current.version === version &&
            cacheRef.current.ssId === ssId
        ) {
            return cacheRef.current.items;
        }
        const ss = patient.getStructureSetById(ssId);
        const items = ss ? ss.rois.map(createRoiListItemView) : [];
        domainTrace.view('useRoiListView miss', {
            version,
            count: items.length,
        });
        cacheRef.current = { version, ssId, items };
        return items;
    });
}
